<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue';

import type {
  FormFlattenKey,
  FormFlattenValue,
  FormSchema,
} from '@modules/forms/components/VForm/types';
import type { ISettingsUploadLogoForm } from '@modules/general-settings/submodules/upload-logo/types';
import VCard from '@components/card/VCard/VCard.vue';
import { useConfig } from '@modules/config/composables';
import { useLogoStore } from '@modules/config/store';
import VForm from '@modules/forms/components/VForm/VForm.vue';
import { EFormInputTypes } from '@modules/forms/enums';
import { TRANSLATION_GROUP_GENERAL_SETTINGS } from '@modules/general-settings/constants';
import {
  TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO,
} from '@modules/general-settings/submodules/upload-logo/constants';
import useSettingsUploadLogoMutation
  from '@modules/general-settings/submodules/upload-logo/mutations/useSettingsUploadLogoMutation';
import { useI18n } from '@modules/i18n/composables';
import { fileToBase64 } from '@utils/file';

const { getTranslation } = useI18n();
const { config } = useConfig();
const { setFavicon, setLogoHeader } = useLogoStore();

const { mutateAsync: uploadLogos, isLoading } = useSettingsUploadLogoMutation();

const values = ref<ISettingsUploadLogoForm>();

const formSchema = computed<FormSchema<ISettingsUploadLogoForm>>(() => ({
  logo_mini: {
    type: EFormInputTypes.SINGLE_FILE,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO, 'CHOOSE_LOGO_MINI').value,
    dragAndDropLabel: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO, 'DROP_LOGO_MINI_HERE').value,
    iconType: 'image',
    value: values.value?.logo_mini,
  },
  logo: {
    type: EFormInputTypes.SINGLE_FILE,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO, 'CHOOSE_LOGO_MAIN').value,
    dragAndDropLabel: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO, 'DROP_LOGO_MAIN_HERE').value,
    iconType: 'image',
    value: values.value?.logo,
  },
  favicon: {
    type: EFormInputTypes.SINGLE_FILE,
    label: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO, 'CHOOSE_FAVICON').value,
    dragAndDropLabel: getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS_UPLOAD_LOGO, 'DROP_FAVICON_HERE').value,
    iconType: 'image',
    value: values.value?.favicon,
  },
}));

async function readFile(file: unknown): Promise<string | undefined> {
  if (file && file instanceof File) {
    try {
      return await fileToBase64(file);
    } catch {}
  }
  return undefined;
}

function handleChange<
  T extends ISettingsUploadLogoForm,
  K extends FormFlattenKey<T>,
>(key: K, value: FormFlattenValue<T, K> | undefined): void {
  if (key === 'favicon') {
    void readFile(value).then((content) => setFavicon(content || config.value?.favicon));
  }

  if (key === 'logo_mini') {
    void readFile(value).then((content) => setLogoHeader(content || config.value?.logo_mini));
  }
}

async function handleSubmit(payload: ISettingsUploadLogoForm): Promise<void> {
  values.value = payload;
  const formData = new FormData();

  for (const [name, value] of Object.entries(payload)) {
    if (value) {
      formData.append(name, value);
    }
  }

  await uploadLogos(formData);
  values.value = undefined;
}

onUnmounted(() => {
  setFavicon(config.value?.favicon);
  setLogoHeader(config.value?.logo_mini);
});
</script>

<template>
  <VCard v-data-qa-auto-id="'AdminGeneralSettingsUploadLogoPage'">
    <VForm
      :schema="formSchema"
      :is-loading="isLoading"
      :submit-btn-label="$getTranslation(TRANSLATION_GROUP_GENERAL_SETTINGS, 'SAVE')"
      @change="handleChange"
      @submit="handleSubmit"
    />
  </VCard>
</template>
